<script setup>
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import { useUserStore } from '@/stores/user';
import { useAdminStore } from '@/stores/admin';
import { useIncentivesStore } from '@/stores/incentives';
import { useRatingsStore } from '@/stores/ratings';
import { usePerformanceStore } from '@/stores/performance';

const userStore = useUserStore();
const adminStore = (()=>{ try { return useAdminStore(); } catch(e){ return null; } })();
const incentivesStore = useIncentivesStore();
const ratingsStore = useRatingsStore();
const performanceStore = usePerformanceStore();

const props = defineProps({
    salesAgentId: { type: Number, default: 0 },
    layout: { type: String, default: 'row', validator: v => ['row','column'].includes(v) },
    idUser: { type: Number, default: 0 },
    idTeam: { type: Number, default: 0 },
    isSalesAgent: { type: Boolean, default: false },
    isSalesTeam: { type: Boolean, default: false },
    showSalesAgent: { type: Boolean, default: false },
    isUserAvatar: { type: Boolean, default: false },
    isPerformance: { type: Boolean, default: false },
    size: { type: Number, default: 64 },
    showPlannedStatus: { type: Boolean, default: false },
    showPoints: { type: Boolean, default: false },
    subline: { type: String, default: '' },
    statusOverride: { type: Object, default: null },
    // hideText=true => render only the avatar (no txt-wrapper)
    hideText: { type: Boolean, default: false },
    // hideRank=true => blende NUR den Rang/Status (sm-status) aus, Name bleibt sichtbar
    hideRank: { type: Boolean, default: false },
    // NEW: hide only the status line, not all text
    hideStatus: { type: Boolean, default: false },
    noPadding: { type: Boolean, default: false },
    noMargin: { type: Boolean, default: false },
    showRoles: { type: Boolean, default: false } // optional: show all roles for current campaign as pills
    ,suppressPopover: { type: Boolean, default: false }
});
// Role priority constant (higher number => higher rank precedence)
const ROLE_PRIORITY = {
    'sma:management': 300,
    'sma:salesteammanager': 200,
    'sma:salesagent': 100
};

const displayStatus = computed(() => {
    if (userInformation.value.currentStatus) {
        return userInformation.value.currentStatus;
    }

    if (!props.isUserAvatar) return '';

    if (userStore.isManagement) return 'Standort Verantwortlicher';
    if (userStore.isWinback) return 'Kundenservice';
    if (userStore.isSalesTeamManager) return 'Area Manager';
    if (userStore.isSalesAgent) return 'Sales Manager';

    return '';
});


const displayedPerformance = computed(() => performanceStore.performance[userStore.currentSelection.performance.display]);

// Safe helpers to avoid calling .find on non-arrays
function safeFind(list, predicate){
    const arr = Array.isArray(list) ? list : [];
    try { return arr.find(predicate); } catch { return undefined; }
}
function safeRoles(list){
    const arr = Array.isArray(list) ? list : [];
    return arr;
}

const userInformation = computed(() => {
    let salesAgentName = null;
    let salesAgentPhoto = 'dummy';
    let salesAgentStatusClass = '';
    let salesAgentStatusName = null;
    let plannedStatusLevelValue = null;
    let plannedStatusName = null;
    let salesAgentIsRookie = false;
    let salesAgentIsStarSeller = false;
    let salesAgentAverageRating = null;
    let idUser = null;
    let idTeam = null;

    let id = null;
    let userName = null;
    let currentStatusClass = null;
    let plannedStatusClass = null;
    let isRookie = false;
    let isStarSeller = false;
    let currentStatus = null;

    // TEAM
    if (props.idTeam) {
        idTeam = props.idTeam;
        const salesTeam = safeFind(incentivesStore.incentive?.salesTeams?.champions, c => Number(props.idTeam) === Number(c.idTeam));
        if (salesTeam) { salesAgentName = salesTeam.salesTeamName; salesAgentPhoto = salesTeam.photo; }
        return { salesAgentStatusName, statusClass: salesAgentStatusClass, salesAgentName, salesAgentAverageRating, salesAgentPhoto, idTeam, salesAgentIsRookie, salesAgentIsStarSeller, plannedStatusLevelValue, plannedStatusName, plannedStatusClass: plannedStatusName ? plannedStatusName.toLowerCase().replaceAll(' ', '-') : '' };
    }

    // USER by idUser or idSalesAgent
    if (props.idUser || props.salesAgentId) {
        let colleague = null;
        if (props.idUser) {
            colleague = safeFind(userStore.colleagues, c => Number(props.idUser) === Number(c.id));
            if(!colleague && Array.isArray(adminStore?.colleagues) && adminStore.colleagues.length){
                colleague = safeFind(adminStore.colleagues, c => Number(props.idUser) === Number(c.id));
            }
        } else if (props.salesAgentId) {
            colleague = safeFind(userStore.colleagues, c => c.isSalesAgent && Number(c.isSalesAgent.id) === Number(props.salesAgentId));
            if(!colleague && Array.isArray(adminStore?.colleagues) && adminStore.colleagues.length){
                colleague = safeFind(adminStore.colleagues, c => c.isSalesAgent && Number(c.isSalesAgent.id) === Number(props.salesAgentId));
            }
            // Fallback: pull minimal data from filterOption.salesAgents when colleague not in colleagues list
            if(!colleague) {
                const sa = safeFind(userStore.filterOption?.salesAgents, a => Number(a.id) === Number(props.salesAgentId));
                if (sa) {
                    colleague = {
                        id: sa.idUser || sa.id,
                        firstName: sa.firstName,
                        lastName: sa.lastName,
                        photo: sa.photo,
                        isSalesAgent: {
                            id: sa.id,
                            isRookie: sa.isRookie == 1,
                            isStarSeller: sa.isStarSeller == 1,
                            salesAgentStatusPlanned: sa.salesAgentStatusPlanned,
                            salesAgentStatusCurrent: sa.salesAgentStatusCurrent,
                        }
                    }
                }
            }
        }
        if(!colleague) return { id: props.idUser || props.salesAgentId, userName: 'Unbekannt', currentStatus: null, statusClass: '', isRookie: false, salesAgentIsRookie: false, isStarSeller: false, salesAgentIsStarSeller: false, salesAgentPhoto: 'dummy.webp', salesAgentName: 'Unbekannt', plannedStatusClass: '' };
        id = colleague.id;
        userName = `${colleague.firstName || ''} ${colleague.lastName || ''}`.trim();
        // collect company / teams / managers info for popover
        let companyName = null;
        const salesTeams = [];
        const salesTeamManagers = [];
        try {
            const roles = Array.isArray(colleague.roles) ? colleague.roles : [];
            for (const r of roles) {
                if (Array.isArray(r.salesTeams)) {
                    for (const st of r.salesTeams) {
                        if (st && st.name && !salesTeams.includes(st.name)) salesTeams.push(st.name);
                        if (st && st.company && st.company.name) companyName = companyName || st.company.name;
                        if (Array.isArray(st.salesTeamManagers)) {
                            for (const m of st.salesTeamManagers) {
                                const mName = `${m.firstName || ''} ${m.lastName || ''}`.trim();
                                if (mName && !salesTeamManagers.includes(mName)) salesTeamManagers.push(mName);
                            }
                        }
                    }
                }
            }
        } catch (e) { /* ignore */ }
        const champion = safeFind(incentivesStore.incentive?.salesAgents?.champions, c => Number(c.idUser) === Number(colleague.id));
        const champIsRookie = champion?.isRookie == 1;
        const champIsStar = champion?.isStarSeller == 1;
        isRookie = (colleague.isSalesAgent?.isRookie == 1) || champIsRookie;
        isStarSeller = (colleague.isSalesAgent?.isStarSeller == 1) || champIsStar;
        salesAgentIsRookie = isRookie;
        salesAgentIsStarSeller = isStarSeller;
        // Get planned and current status from colleague.isSalesAgent
        plannedStatusName = colleague.isSalesAgent?.salesAgentStatusPlanned || null;
        currentStatus = colleague.isSalesAgent?.salesAgentStatusCurrent || null;
        currentStatusClass = currentStatus ? currentStatus.toLowerCase().replaceAll(' ', '-') : '';
        plannedStatusClass = plannedStatusName ? plannedStatusName.toLowerCase().replaceAll(' ', '-') : '';
        salesAgentPhoto = colleague.photo || ('user_' + colleague.id + '.webp');
        return { id, userName, currentStatus, statusClass: currentStatusClass, salesAgentAverageRating, salesAgentPhoto, salesAgentName: userName, idUser: id, salesAgentIsRookie, salesAgentIsStarSeller, plannedStatusLevelValue, plannedStatusName, plannedStatusClass, isRookie: salesAgentIsRookie, isStarSeller: salesAgentIsStarSeller, companyName, salesTeams, salesTeamManagers };
    }

    // salesAgentId branch (selection context)

    if (props.salesAgentId) {
        const salesAgent = safeFind(userStore.filterOption?.salesAgents, a => Number(a.id) === Number(props.salesAgentId));
        if(!salesAgent) return { salesAgentName: `${userStore.firstName} ${userStore.lastName}`, salesAgentPhoto: userStore.photo };
        idUser = salesAgent.id;
        salesAgentPhoto = salesAgent.photo;
        // Get planned and current status from salesAgent object if available
        plannedStatusName = salesAgent.salesAgentStatusPlanned || null;
        currentStatus = salesAgent.salesAgentStatusCurrent || null;
        plannedStatusLevelValue = safeFind(displayedPerformance.value?.status?.statusList, s => s.name === plannedStatusName)?.levelValue ?? null;
        const championEntry = safeFind(incentivesStore.incentive?.salesAgents?.champions, c => Number(props.salesAgentId) === Number(c.idUser));
        salesAgentIsRookie = championEntry?.isRookie == 1;
        salesAgentIsStarSeller = championEntry?.isStarSeller == 1;
        salesAgentAverageRating = safeFind(ratingsStore.rating?.week?.bySalesAgent, a => Number(a.id) === Number(props.salesAgentId))?.average.ratings;
        if (props.showPlannedStatus) {
            salesAgentName = `${salesAgent.firstName} ${salesAgent.lastName}`;
        } else if (props.isUserAvatar === true) {
            salesAgentName = `${userStore.firstName} ${userStore.lastName}`;
            salesAgentPhoto = userStore.photo;
            salesAgentStatusName = safeFind(incentivesStore.incentive?.salesAgents?.champions, c => Number(userStore.id) === Number(c.idUser))?.salesAgentStatusCurrent;
            salesAgentIsRookie = safeFind(incentivesStore.incentive?.salesAgents?.champions, c => Number(userStore.id) === Number(c.idUser))?.isRookie == 1;
        } else if (userStore.isManagement && userStore.currentSelection.scope !== 'salesAgent' && !props.showSalesAgent) {
            salesAgentName = `${userStore.firstName} ${userStore.lastName}`;
            salesAgentPhoto = userStore.photo;
            salesAgentStatusName = safeFind(incentivesStore.incentive?.salesAgents?.champions, c => Number(salesAgent.idUser) === Number(c.idUser))?.salesAgentStatusCurrent;
        } else {
            salesAgentName = `${salesAgent.firstName} ${salesAgent.lastName}`;
            if (props.showSalesAgent) {
                salesAgentStatusName = safeFind(incentivesStore.incentive?.salesAgents?.champions, c => Number(salesAgent.idUser) === Number(c.idUser))?.salesAgentStatusCurrent;
            } else {
                salesAgentStatusName = safeFind(incentivesStore.incentive?.salesAgents?.champions, c => Number(userStore.id) === Number(c.idUser))?.salesAgentStatusCurrent;
            }
        }
        salesAgentStatusClass = salesAgentStatusName?.toLowerCase().replaceAll(' ', '-');
        plannedStatusClass = plannedStatusName ? plannedStatusName.toLowerCase().replaceAll(' ', '-') : '';
        return { salesAgentStatusName, statusClass: salesAgentStatusClass, salesAgentName, salesAgentAverageRating, salesAgentPhoto, idUser, salesAgentIsRookie, salesAgentIsStarSeller, plannedStatusLevelValue, plannedStatusName, plannedStatusClass, currentStatus, isRookie: salesAgentIsRookie, isStarSeller: salesAgentIsStarSeller };
    }

    return { salesAgentStatusName, statusClass: salesAgentStatusClass, salesAgentName, salesAgentAverageRating, salesAgentPhoto, idUser, salesAgentIsRookie, salesAgentIsStarSeller, plannedStatusLevelValue, plannedStatusName, currentStatus, isRookie: salesAgentIsRookie, isStarSeller: salesAgentIsStarSeller };
});

// Role (campaign-specific) resolution for rank icon logic
const campaignRoleName = computed(() => {
    const idCampaignLogin = userStore?.idCampaignLogin != null ? String(userStore.idCampaignLogin) : null;
    if(!idCampaignLogin) return null;
    let colleague = null;
    // Prioritize explicit props
    if (props.idUser) {
        colleague = safeFind(userStore.colleagues, c => Number(props.idUser) === Number(c.id)) || safeFind(adminStore?.colleagues, c => Number(props.idUser) === Number(c.id));
    } else if (props.salesAgentId) {
        colleague = safeFind(userStore.colleagues, c => c.isSalesAgent && Number(c.isSalesAgent.id) === Number(props.salesAgentId)) || safeFind(adminStore?.colleagues, c => c.isSalesAgent && Number(c.isSalesAgent.id) === Number(props.salesAgentId));
    } else if (props.isUserAvatar) {
        colleague = safeFind(userStore.colleagues, c => Number(c.id) === Number(userStore.id)) || safeFind(adminStore?.colleagues, c => Number(c.id) === Number(userStore.id));
    }
    if(!colleague) return null;
    const rolesForCampaign = safeRoles(colleague.roles).filter(r => String(r.idCampaign) === idCampaignLogin);
    if(!rolesForCampaign.length) return null;
    const normalized = rolesForCampaign
        .map(r => ({ raw: r, name: (r.name||'').toLowerCase(), prio: ROLE_PRIORITY[(r.name||'').toLowerCase()] ?? 0 }))
        .sort((a,b) => b.prio - a.prio);
    return normalized[0].name || null;
});

// Determine effective role considering current campaign + global fallbacks
const effectiveRole = computed(() => {
    const idCampaignLogin = userStore?.idCampaignLogin != null 
        ? String(userStore.idCampaignLogin) 
        : null;

    let colleague = null;

    // aktuellen User / Zieluser suchen
    if (props.idUser) {
        colleague =
            safeFind(userStore.colleagues, c => Number(props.idUser) === Number(c.id)) ||
            safeFind(adminStore?.colleagues, c => Number(props.idUser) === Number(c.id));

    } else if (props.salesAgentId) {
        colleague =
            safeFind(userStore.colleagues, c => 
                c.isSalesAgent && Number(c.isSalesAgent.id) === Number(props.salesAgentId)
            ) ||
            safeFind(adminStore?.colleagues, c =>
                c.isSalesAgent && Number(c.isSalesAgent.id) === Number(props.salesAgentId)
            );

    } else if (props.isUserAvatar) {
        colleague =
            safeFind(userStore.colleagues, c => Number(c.id) === Number(userStore.id)) ||
            safeFind(adminStore?.colleagues, c => Number(c.id) === Number(userStore.id));
    }


    // Rollen aus dem Kollegenobjekt
    if (colleague && Array.isArray(colleague.roles)) {

        const roles = colleague.roles
            .filter(r => !idCampaignLogin || String(r.idCampaign) === idCampaignLogin)
            .map(r => {
                const name = String(r.name || '').toLowerCase();

                return {
                    name,
                    priority: ROLE_PRIORITY[name] ?? 0
                };
            })
            .sort((a,b) => b.priority - a.priority);


        if (roles.length && roles[0].priority > 0) {
            return roles[0].name;
        }
    }


    // Fallback nur wenn keine Rolle gefunden wurde
    if (props.isUserAvatar) {
        if (userStore.isManagement) return 'sma:management';
        if (userStore.isSalesTeamManager) return 'sma:salesteammanager';
        if (userStore.isSalesAgent) return 'sma:salesagent';
    }


    return null;
});

// All roles (sorted) for optional display
const rolesForCampaignSorted = computed(() => {
    const idCampaignLogin = userStore?.idCampaignLogin != null ? String(userStore.idCampaignLogin) : null;
    if(!idCampaignLogin) return [];
    let colleague = null;
    if (props.idUser) {
        colleague = safeFind(userStore.colleagues, c => Number(props.idUser) === Number(c.id)) || safeFind(adminStore?.colleagues, c => Number(props.idUser) === Number(c.id));
    } else if (props.salesAgentId) {
        colleague = safeFind(userStore.colleagues, c => c.isSalesAgent && Number(c.isSalesAgent.id) === Number(props.salesAgentId)) || safeFind(adminStore?.colleagues, c => c.isSalesAgent && Number(c.isSalesAgent.id) === Number(props.salesAgentId));
    } else if (props.isUserAvatar) {
        colleague = safeFind(userStore.colleagues, c => Number(c.id) === Number(userStore.id)) || safeFind(adminStore?.colleagues, c => Number(c.id) === Number(userStore.id));
    }
    if(!colleague) return [];
    return safeRoles(colleague.roles)
        .filter(r => String(r.idCampaign) === idCampaignLogin)
        .map(r => ({ name: (r.name||'').toLowerCase(), prio: ROLE_PRIORITY[(r.name||'').toLowerCase()] ?? 0 }))
        .sort((a,b) => b.prio - a.prio)
        .map(r => ({
            name: r.name,
            short: r.name === 'sma:management' ? 'Mgmt' : r.name === 'sma:salesteammanager' ? 'Area' : r.name === 'sma:salesagent' ? 'SM' : r.name
        }));
});

// Decide which rank frame/icon category to render
// Returns: 'management' | 'area-manager' | 'status'
const rankIcon = computed(() => {
    const r = effectiveRole.value; // normalized or null
    // Management always highest precedence
    if (r === 'sma:management' || (userStore.isManagement && props.isUserAvatar)) return 'management';

    // Area manager precedence next
    if (r === 'sma:salesteammanager' || (props.isUserAvatar && userStore.isSalesTeamManager)) return 'area-manager';

    // Explicit team avatar props
    if (props.isSalesTeam || props.idTeam) return 'area-manager';

    // If role array (campaignRoleName) matched patterns (safety net when not normalized above)
    if (r && (/(^|:)salesteammanager$/.test(r) || /(area[-]?manager|team[-]?manager)/.test(r))) return 'area-manager';

    return 'status';
});

// Image fallback chain
const photoChain = ref([]);
const currentPhotoIndex = ref(0);
const currentPhoto = computed(()=> photoChain.value[currentPhotoIndex.value] || '/avatars/dummy.webp');

function buildPhotoChain(){
    if(!userInformation.value){ photoChain.value = []; currentPhotoIndex.value = 0; return; }
    const chain = [];
    // Always try explicit photo first if it's already a full URL or custom
    const direct = userInformation.value?.salesAgentPhoto;
    if(direct && !['dummy','dummy.webp','dummy.jpg'].includes(direct)) chain.push(direct);
    const derivedId = userInformation.value?.id || userInformation.value?.idUser || props.idUser || props.salesAgentId;
    if(derivedId){
        // New order: try jpg first (primary), no remote webp fallback anymore
        chain.push(`user_${derivedId}.jpg`);
    }
    photoChain.value = chain.filter((v,i)=> chain.indexOf(v)===i);
    currentPhotoIndex.value = 0;
}
watch(()=> userInformation.value?.id, buildPhotoChain, { immediate:true });

const computedPhotoSrc = computed(()=>{
    const p = currentPhoto.value;
    if(/^https?:\/\//i.test(p)) return p;
    if(p.startsWith('/avatars/') || p.startsWith('avatars/')) return p.startsWith('/')? p : '/' + p;
    if(p.startsWith('user_')) return `/avatar/${p.replace(/\.webp$/i, '.jpg')}`;
    return '/avatars/dummy.webp';
});

function handleImageError(evt){
    if(currentPhotoIndex.value + 1 < photoChain.value.length){
        currentPhotoIndex.value++;
        evt.target.src = computedPhotoSrc.value;
        return;
    }
    if(!/\/avatars\/dummy\.webp$/.test(evt.target.src)){
        evt.target.src = '/avatars/dummy.webp';
        // Diagnostic log for missing avatar
        const id = props.idUser || props.salesAgentId || props.idTeam;
        console.warn('[Avatar Fallback] No avatar found for ID:', id, '| photoChain:', photoChain.value, '| userInformation:', userInformation.value);
    }
}

function initCompareSliders(){
    const compares = document.querySelectorAll('.compare');
    compares.forEach(($compare, idx) => {
        if(!$compare.querySelector('.compare__mask')) return;
        const $input = $compare.querySelector('input');
        if($input && !$input._hasListener){
            $input.addEventListener('input', () => {
                $compare.style.setProperty('--mask-width', `${$input.value}%`);
            });
            $input.value = 0;
            $compare.style.setProperty('--mask-width', `${$input.value}%`);
            $input._hasListener = true;
            let direction = 1;
            setInterval(()=>{
                let start = direction === 1 ? 0 : 100;
                let end = direction === 1 ? 100 : 0;
                let duration = 1000; let startTime = null;
                function animateSlider(ts){
                    if(!startTime) startTime = ts;
                    let progress = Math.min((ts - startTime)/duration, 1);
                    let value = start + (end - start) * progress;
                    $input.value = value;
                    $compare.style.setProperty('--mask-width', `${value}%`);
                    if(progress < 1){ requestAnimationFrame(animateSlider); } else { direction *= -1; }
                }
                requestAnimationFrame(animateSlider);
            }, 5000);
        }
    });
}

onMounted(()=>{ nextTick(initCompareSliders); });
watch(()=> userInformation.value?.salesAgentIsStarSeller, async ()=>{ await nextTick(); initCompareSliders(); });

// Popover state for hover colleagues card
const showPopover = ref(false);
const popoverX = ref(0);
const popoverY = ref(0);
const popoverStyle = ref({});
const avatarPopoverRef = ref(null);
let popoverTimeout = null;
const emit = defineEmits(['click','openMenu']);

// small helper: map status class to a rank/status icon path
import { computed as _computed } from 'vue';
const popoverStatusIconSrc = _computed(() => {
    const cls = userInformation.value?.statusClass || userInformation.value?.plannedStatusClass || '';
    if (!cls) return null;
    const iconNames = ['basic','advanced-silver','advanced-gold','professional-silver','professional-gold','elite-silver','elite-gold','star-seller'];
    const match = iconNames.find(n => cls === n || cls.includes(n));
    if (!match) return null;
    return `/assets/imgs/ranks/svg/${match}.svg`;
});

const areaManagerName = computed(() => {
    const teamId = Number(userStore.currentSelection?.salesTeam?.id);
    if (!Number.isFinite(teamId) || teamId < 0) return null;
    const all = Array.isArray(userStore.colleagues) ? userStore.colleagues : (Array.isArray(adminStore?.colleagues) ? adminStore.colleagues : []);
    const mgr = all.find(c => Array.isArray(c.roles) && c.roles.some(r => r.name === 'SMA:salesTeamManager' && Number(r.team?.id) === teamId));
    if(!mgr) return null;
    return `${mgr.firstName || ''} ${mgr.lastName || ''}`.trim();
});

// Build avatar menu info similar to Nav.vue to keep popover and dropdown consistent
const avatarMenuInfo = computed(() => {
    const all = Array.isArray(userStore.colleagues) ? userStore.colleagues : (Array.isArray(adminStore?.colleagues) ? adminStore.colleagues : []);
    let colleague = all.find(c => Number(c.id) === Number(userStore.id));
    if (!colleague && Array.isArray(adminStore?.colleagues)) colleague = adminStore.colleagues.find(c => Number(c.id) === Number(userStore.id));
    const name = colleague ? `${colleague.firstName || ''} ${colleague.lastName || ''}`.trim() : `${userStore.firstName || ''} ${userStore.lastName || ''}`.trim();
    const status = colleague?.isSalesAgent?.salesAgentStatusCurrent || colleague?.isSalesAgent?.salesAgentStatusPlanned || null;
    let companyName = null;
    const salesTeams = [];
    const salesTeamManagers = [];
    try {
        const roles = Array.isArray(colleague?.roles) ? colleague.roles : [];
        for (const r of roles) {
            if (Array.isArray(r.salesTeams)) {
                for (const st of r.salesTeams) {
                    if (st && st.name && !salesTeams.includes(st.name)) salesTeams.push(st.name);
                    if (st && st.company && st.company.name) companyName = companyName || st.company.name;
                    if (Array.isArray(st.salesTeamManagers)) {
                        for (const m of st.salesTeamManagers) {
                            const mName = `${m.firstName || ''} ${m.lastName || ''}`.trim();
                            if (mName && !salesTeamManagers.includes(mName)) salesTeamManagers.push(mName);
                        }
                    }
                }
            }
        }
    } catch (e) { /* ignore */ }
    const statusClass = (function(){ const s = status || ''; if (!s) return ''; return s.toLowerCase().replaceAll(' ', '-'); })();
    // determine if this colleague is an Area Manager
    let isAreaManager = false;
    let isManagement = false;
    try {
        if (Array.isArray(colleague?.roles)) {
            for (const r of colleague.roles) {

                const roleName = String(r?.name || '').toLowerCase();

                if (roleName.includes('management')) {
                    isManagement = true;
                }

                if (roleName.includes('salesteammanager')) {
                    isAreaManager = true;
                }
            }
        }
    } catch (e) {}
    if (!isAreaManager && salesTeamManagers.includes(name)) isAreaManager = true;

    const salesTeamManagersFiltered = (salesTeamManagers || []).filter(m => m !== name);

    return { 
        name, 
        status, 
        statusClass, 
        statusIcon: null, 
        companyName, 
        salesTeams, 
        salesTeamManagers, 
        salesTeamManagersFiltered, 
        isAreaManager,
        isManagement
    };
});

const selectedSalesManagerName = computed(() => {
    if (userStore.currentSelection?.scope !== 'salesAgent') return null;
    const agent = userStore.currentSelection?.salesAgent;
    if (!agent) return null;
    const full = [agent.firstName, agent.lastName].filter(Boolean).join(' ').trim();
    return full || agent.name || null;
});

const selectedSalesManagerStatus = computed(() => {
    const agentId = userStore.currentSelection?.salesAgent?.id;
    if (!agentId) return null;
    const sa = safeFind(userStore.filterOption?.salesAgents, a => Number(a.id) === Number(agentId));
    return sa?.salesAgentStatusCurrent || sa?.salesAgentStatusPlanned || null;
});

function onAvatarEnter(evt){
    // Only show the hover popover for the main navigation avatar (avoid popovers in lists/previews)
    if (!props.isUserAvatar) return;
    if (props.suppressPopover) return;
    // Ensure this instance is in the nav context (root .sm-wrapper.nav)
    let el = evt && (evt.currentTarget || evt.target);
    let foundNav = false;
    try {
        let loops = 0;
        while (el && loops < 10) {
            if (el.classList && el.classList.contains && el.classList.contains('nav')) { foundNav = true; break; }
            el = el.parentElement;
            loops++;
        }
    } catch (e) { foundNav = false; }
    if (!foundNav) return;

    if(popoverTimeout) clearTimeout(popoverTimeout);
    popoverTimeout = setTimeout(async ()=>{
        showPopover.value = true;
        await nextTick();
        updatePopoverPosition(evt);
    }, 140);
}
function onAvatarMove(evt){ if(showPopover.value) updatePopoverPosition(evt); }
function onAvatarLeave(){
    if(popoverTimeout) clearTimeout(popoverTimeout);
    // schedule delayed close to allow moving into the popover without it closing immediately
    popoverTimeout = setTimeout(()=>{ showPopover.value = false; popoverTimeout = null; }, 180);
}
function onAvatarClick(evt){
    // When clicked (especially in the nav), close the hover popover and let parent open the avatar menu
    if(popoverTimeout) clearTimeout(popoverTimeout);
    showPopover.value = false;
    // Emit native-style click so parent listeners (e.g. Nav.vue @click) toggle menus
    emit('click', evt);
}
function onPopoverEnter(){ if(popoverTimeout) clearTimeout(popoverTimeout); showPopover.value = true; }
function onPopoverLeave(){
    if(popoverTimeout) clearTimeout(popoverTimeout);
    popoverTimeout = setTimeout(()=>{ showPopover.value = false; popoverTimeout = null; }, 180);
}
function updatePopoverPosition(evt){
    const offset = 12;
    const margin = 8;
    const popoverEl = avatarPopoverRef.value || document.querySelector('.avatar-popover');
    const popoverWidth = popoverEl ? Math.min(popoverEl.offsetWidth || 280, 800) : 280;
    const popoverHeight = popoverEl ? (popoverEl.offsetHeight || 80) : 80;
    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;

    // Prefer anchoring to the avatar bounding rect when possible (more stable than raw mouse coords)
    let anchorRect = null;
    try {
        anchorRect = (evt && evt.currentTarget && evt.currentTarget.getBoundingClientRect && evt.currentTarget.getBoundingClientRect()) || (evt && evt.target && evt.target.getBoundingClientRect && evt.target.getBoundingClientRect()) || null;
    } catch (e) { anchorRect = null; }
    // Fallback: try document.elementFromPoint to find nearest .sm-wrapper ancestor
    if (!anchorRect && evt && evt.clientX != null && evt.clientY != null && typeof document !== 'undefined' && document.elementFromPoint) {
        try {
            let el = document.elementFromPoint(evt.clientX, evt.clientY);
            let loops = 0;
            while(el && loops < 10) {
                if (el.classList && el.classList.contains && el.classList.contains('sm-wrapper')) {
                    anchorRect = el.getBoundingClientRect();
                    break;
                }
                el = el.parentElement;
                loops++;
            }
        } catch (e) { /* ignore */ }
    }

    let preferredLeft = evt && evt.clientX ? evt.clientX + offset : margin;
    let top = evt && evt.clientY ? evt.clientY + offset : margin;

    if (anchorRect) {
        if (props.isUserAvatar) {
            // For navigation avatar: place popover above the avatar and centered horizontally
            preferredLeft = Math.round(anchorRect.left + (anchorRect.width / 2) - (popoverWidth / 2));
            top = Math.round(anchorRect.top - popoverHeight - offset);
            // If not enough room above, place below the anchor instead
            if (top < margin) {
                top = Math.round(anchorRect.bottom + offset);
            }
        } else {
            // Default: position to the right of the anchor and vertically centered
            preferredLeft = Math.round(anchorRect.right + offset);
            top = Math.round(anchorRect.top + (anchorRect.height / 2) - (popoverHeight / 2));
        }
    }

    // Clamp top to viewport
    top = Math.max(margin, Math.min(top, viewportH - popoverHeight - margin));

    // Try placing to the right; if not enough space, place to the left of anchor or clamp
    let left = preferredLeft;
    if (left + popoverWidth + margin > viewportW) {
        if (anchorRect) {
            left = Math.round(anchorRect.left - popoverWidth - offset);
        } else {
            left = Math.min(Math.max(margin, viewportW - popoverWidth - margin), margin);
        }
    }
    // Final clamp
    left = Math.max(margin, Math.min(left, viewportW - popoverWidth - margin));

    popoverStyle.value = { left: left + 'px', top: top + 'px' };
}
</script>

<template>
        <div v-bind="$attrs" @mouseenter="onAvatarEnter" @mouseleave="onAvatarLeave" @mousemove="onAvatarMove" @click="onAvatarClick" :class="['sm-wrapper nav', `layout-${props.layout}`, {'isSalesAgent': !userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback, 'no-padding': noPadding, 'no-margin': noMargin}]" :style="`--avatar-size: ${props.size}px;`">
    <div :class="['rank', { 'area-manager': rankIcon==='area-manager', 'management': rankIcon==='management' }]">
            <p class="rating-value">{{ userInformation.salesAgentAverageRating }}</p>
            <div class="imgWrapper">
             <img class="avatar" :src="computedPhotoSrc" :title="userInformation.salesAgentName || userInformation.userName" :alt="userInformation.salesAgentName || userInformation.userName" @error="handleImageError" loading="lazy" decoding="async" />
                <div v-if="props.isUserAvatar" class="avatar-help" aria-hidden="true">?</div>
                <div class="compare" style="overflow: visible;">
                    <!-- Rank Icon Logic: management / area-manager override status icons. Falls keine Managerrolle => Status Icons -->
                    <template v-if="rankIcon==='management'">
                        <img class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/management.svg" />
                    </template>
                    <template v-else-if="rankIcon==='area-manager'">
                        <img class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/area-manager.svg" />
                    </template>
                    <template v-else>
                        <template v-if="(props.statusOverride?.class || (showPlannedStatus ? userInformation.plannedStatusClass : userInformation.statusClass)) !== '---'">
                            <img v-if="(props.statusOverride?.class || (showPlannedStatus ? userInformation.plannedStatusClass : userInformation.statusClass)) === 'basic'" class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/basic.svg" />
                            <img v-else-if="(props.statusOverride?.class || (showPlannedStatus ? userInformation.plannedStatusClass : userInformation.statusClass)) === 'advanced-silver'" class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/advanced-silver.svg" />
                            <img v-else-if="(props.statusOverride?.class || (showPlannedStatus ? userInformation.plannedStatusClass : userInformation.statusClass)) === 'advanced-gold'" class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/advanced-gold.svg" />
                            <img v-else-if="(props.statusOverride?.class || (showPlannedStatus ? userInformation.plannedStatusClass : userInformation.statusClass)) === 'professional-silver'" class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/professional-silver.svg" />
                            <img v-else-if="(props.statusOverride?.class || (showPlannedStatus ? userInformation.plannedStatusClass : userInformation.statusClass)) === 'professional-gold'" class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/professional-gold.svg" />
                            <img v-else-if="(props.statusOverride?.class || (showPlannedStatus ? userInformation.plannedStatusClass : userInformation.statusClass)) === 'elite-silver'" class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/elite-silver.svg" />
                            <img v-else-if="(props.statusOverride?.class || (showPlannedStatus ? userInformation.plannedStatusClass : userInformation.statusClass)) === 'elite-gold'" class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/elite-gold.svg" />
                            <img v-else-if="(props.statusOverride?.class || (showPlannedStatus ? userInformation.plannedStatusClass : userInformation.statusClass)) === 'star-seller'" class="rankIcon compare__image-one" src="@/assets/imgs/ranks/svg/star-seller.svg" />
                        </template>
                    </template>
                </div>
            </div>
            <!-- {{ userInformation }} -->
            <div class="rookie" v-if="userInformation.salesAgentIsRookie"><img src="@/assets/imgs/icons/chicken-with-egg.png" alt="Rookie" /></div>
        </div>

    <!-- Combined logic for hideText/hideRank: 
         - hideText=true: show only avatar (no name, no rank)
         - hideText=false, hideRank=true: show only name
         - hideText=false, hideRank=false: show name and rank/status
    -->
    <div v-if="!hideText" class="txt-wrapper">
        <template v-if="isPerformance">
            <template v-if="showPoints">
                <p v-if="userStore.isSalesTeamManager || userStore.isManagement" class="sm-name">
                    {{ userInformation.salesAgentStatusName
                       || displayedPerformance.status.plannedStatus
                       || (userStore.isSalesTeamManager && userStore.currentSelection.scope !== 'salesAgent' ? '-Area-' : '—') }}
                </p>
                <p v-else class="sm-name">
                    {{ userInformation.salesAgentStatusName || displayedPerformance.status.plannedStatus || '—' }}
                </p>
                <p v-if="userStore.isSalesTeamManager || userStore.isManagement && userStore.currentSelection.scope !== 'salesAgent'" class="sm-status">⌀{{ displayedPerformance.status.statusList.find(s => s.name === displayedPerformance.status.plannedStatus).levelValue }}€ pro Punkt</p>
                <p v-else>{{ displayedPerformance.status.statusList.find(s => s.name === displayedPerformance.status.plannedStatus).levelValue }}€ pro Punkt</p>
            </template>
            <template v-else>
                <p v-if="showPlannedStatus" class="sm-name">{{ userInformation.plannedStatusName || userInformation.salesAgentName || userInformation.userName || '' }}</p>
                <p v-else class="sm-name">{{ userInformation.currentStatus || userInformation.salesAgentName || userInformation.userName || '' }}</p>
                <p class="sm-name">{{ showPlannedStatus && (userStore.isManagement || userStore.isSalesTeamManager) ? 'Summe Sales Manager' : (showPlannedStatus ? 'Geplanter Status' : 'Aktueller Status') }}</p>
            </template>
        </template>
        <template v-else>
            <p class="sm-name">{{ props.statusOverride?.name || userInformation.userName }}</p>
            <!-- Only show rank/status if hideRank is false -->
            <template v-if="!props.hideStatus && !props.hideRank">
                <p v-if="effectiveRole === 'sma:management' && isUserAvatar" class="sm-status">
                    Standort Verantwortlicher
                </p>
                <p v-else-if="effectiveRole === 'sma:salesteammanager' && isUserAvatar" class="sm-status">
                    Area Manager
                </p>
                <p v-else-if="effectiveRole === 'sma:salesagent' && isUserAvatar" class="sm-status">
                    Sales Manager
                </p>
                <template v-else-if="$slots.default">
                    <slot />
                </template>
                <div v-else-if="userInformation.salesAgentIsStarSeller" class="compare" style="overflow: visible;">
                    <p class="sm-status compare__image-one">{{ userInformation.currentStatus }}</p>
                    <div class="compare__mask">
                        <p class="sm-status compare__image-two">Star Seller</p>
                    </div>
                    <input class="compare__input" type="range" min="0" step="0.5" max="100" value="50" />
                </div>
                <p v-else-if="!props.subline" class="sm-status">{{ displayStatus }}</p>
                <p v-else class="sm-status">{{ props.subline }}</p>
            </template>
            <!-- If hideRank is true, do NOT show rank/status, only name -->
        </template>
        <div v-if="showRoles && rolesForCampaignSorted.length" class="roles-badge">
            <span v-for="r in rolesForCampaignSorted" :key="r.name" class="role-pill">{{ r.short }}</span>
        </div>
    </div>
            </div>

            <!-- Hover Popover -->
            <div v-if="showPopover" ref="avatarPopoverRef" class="avatar-popover" :style="popoverStyle" @mouseenter="onPopoverEnter" @mouseleave="onPopoverLeave">
                <div class="popover-inner">

                    <!-- Own user block -->
                    <div class="pop-user-row">
                        <div class="pop-avatar-wrap">
                            <userAvatar :size="44" :isUserAvatar="true" :idUser="userStore.id" :hide-text="true" :no-margin="true" :no-padding="true" :suppress-popover="true" />
                        </div>
                        <div class="pop-user-info">
                            <div class="pop-user-name">{{ avatarMenuInfo.name }}</div>
                            <div class="pop-meta-row">
                                <span class="pop-value">
                                    {{
                                        avatarMenuInfo.isManagement
                                            ? 'Standort Verantwortlicher'
                                            : avatarMenuInfo.isAreaManager
                                                ? 'Area Manager'
                                                : (avatarMenuInfo.status || 'Sales Manager')
                                    }}
                                </span>
                            </div>
                            <div v-if="avatarMenuInfo.companyName" class="pop-meta-row"><span class="pop-label">Standort:</span><span class="pop-value">{{ avatarMenuInfo.companyName }}</span></div>
                            <div v-if="avatarMenuInfo.salesTeams?.length" class="pop-meta-row"><span class="pop-label">Team:</span><span class="pop-value">{{ avatarMenuInfo.salesTeams.join(', ') }}</span></div>
                            <div v-if="avatarMenuInfo.salesTeamManagersFiltered?.length" class="pop-meta-row"><span class="pop-label">Area&nbsp;Manager:</span><span class="pop-value">{{ avatarMenuInfo.salesTeamManagersFiltered.join(', ') }}</span></div>
                        </div>
                    </div>

                    <!-- Selected SM block (Area Manager only) -->
                    <div v-if="(avatarMenuInfo.isAreaManager || avatarMenuInfo.isManagement) && selectedSalesManagerName" class="pop-selected-block">
                        <div class="pop-selected-label">Ausgewählter Sales Manager</div>
                        <div class="pop-user-row pop-user-row--sm">
                            <div class="pop-avatar-wrap pop-avatar-wrap--sm">
                                <userAvatar :size="36" :salesAgentId="userStore.currentSelection.salesAgent?.id" :hide-text="true" :no-margin="true" :no-padding="true" :suppress-popover="true" />
                            </div>
                            <div class="pop-user-info">
                                <div class="pop-user-name">{{ selectedSalesManagerName }}</div>
                                <div class="pop-meta-row"><span class="pop-value">{{ selectedSalesManagerStatus || 'Sales Manager' }}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
</template>

<style lang="scss" scoped>
.no-padding.sm-wrapper, .sm-wrapper.no-padding { padding:0 !important; }
.no-margin.sm-wrapper, .sm-wrapper.no-margin { margin:0 !important; }
  .sm-wrapper .txt-wrapper .sm-name{
    width: max-content;
  }

    .status-label {
        display: flex;
        align-items: center;
        font-size: 0.95rem;
        font-weight: 600;
        border-radius: 6px;
        padding: 0.2em 0.7em;
        margin: 0.1em 0;
        width: fit-content;
        min-width: 120px;
        justify-content: center;
        gap: 0.5em;
    }

    .status-label--fullwidth {
        width: 100%;
        justify-content: center;
    }

    .status-label--red {
        background: #fbeaea;
        color: #c00;
        border: 1px solid #c00;
    }

    .status-label--green {
        background: #eafbe9;
        color: #1a7f37;
        border: 1px solid #1a7f37;
    }

    .status-label--yellow {
        background: #fffbe6;
        color: #b59b00;
        border: 1px solid #b59b00;
    }

    .status-label__icon {
        width: 1.1em;
        height: 1.1em;
        margin-right: 0.3em;
    }

    /* ── Popover card ──────────────────────────────────────────── */
    .avatar-popover {
        position: fixed;
        z-index: 99999;
        pointer-events: auto;
        animation: avatar-popover-in 150ms cubic-bezier(.16,1,.3,1);
    }
    .avatar-popover .popover-inner {
        min-width: 260px;
        max-width: 440px;
        background: #ffffff;
        border-radius: 16px;
        padding: 1rem 1.1rem;
        color: #0f172a;
        border: 1px solid rgba(15,23,42,0.07);
        box-shadow:
            0 0 0 1px rgba(255,255,255,0.55) inset,
            0 4px 6px rgba(15,23,42,0.04),
            0 12px 28px rgba(15,23,42,0.13),
            0 28px 56px rgba(15,23,42,0.08);
    }

    /* ── User row (own + selected SM) ──────────────────────────── */
    .pop-user-row { display:flex; gap:0.85rem; align-items:flex-start; }
    .pop-user-row--sm { margin-top: 0.6rem; }

    /* Avatar wrap with subtle ring */
    .pop-avatar-wrap {
        flex: 0 0 auto;
        border-radius: 12px;
        // box-shadow: 0 0 0 2px rgba(99,102,241,0.18), 0 4px 14px rgba(15,23,42,0.12);
        overflow: hidden;
        line-height: 0;
    }
    .pop-avatar-wrap--sm {
        border-radius: 10px;
        // box-shadow: 0 0 0 2px rgba(99,102,241,0.12), 0 3px 8px rgba(15,23,42,0.10);
    }

    /* User info column */
    .pop-user-info { display:flex; flex-direction:column; gap:0px; min-width:0; padding-top:2px; }
    .pop-user-name { font-weight:800; font-size:1rem; color:#07101a; letter-spacing:-0.3px; line-height:1.15; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

    /* Role badge pill */
    .pop-role-badge {
        display: inline-flex;
        align-items: center;
        font-size: 0.72rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 2px 8px;
        border-radius: 999px;
        width: fit-content;
    }
    .badge--area { background: rgba(#28817e, .25); color: #000; }
    .badge--sm   { background: rgba(#97d2d4, .25); color: #97d2d4; }

    /* Meta label/value rows */
    .pop-meta-row { display:flex; gap:6px; align-items:baseline; }
    .pop-label {
        font-size: 0.68rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #94a3b8;
        flex: 0 0 auto;
        white-space: nowrap;
    }
    .pop-value { font-size:0.8rem; color:#475569; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; margin: 0; }

    /* ── Selected Sales Manager section ────────────────────────── */
    .pop-selected-block {
        margin-top: 0.85rem;
        padding-top: 0.75rem;
        border-top: 1px solid rgba(15,23,42,0.06);
    }
    .pop-selected-label {
        font-size: 0.68rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: #94a3b8;
        margin-bottom: 0.3rem;
    }

    /* Small help/questionmark overlay on avatar when hovering */
    .imgWrapper { position: relative; }
    .avatar-help { position: absolute; right: -6px; top: 0; width:18px; height:18px; display:flex; align-items:center; justify-content:center; border-radius:50%; background:rgba(2,6,23,0.85); color:#fff; font-size:11px; font-weight:700; opacity:0; transform:scale(.9); transition:opacity .12s ease, transform .12s ease; pointer-events:none; }
    .sm-wrapper.nav:hover .avatar-help { opacity:1; transform:scale(1); }
    .sm-wrapper { cursor: default; }
    .sm-wrapper.nav { cursor: pointer; }

    @keyframes avatar-popover-in {
        from { opacity: 0; transform: translateY(6px) scale(0.98); }
        to { opacity: 1; transform: translateY(0) scale(1); }
    }
</style>